/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { 
  Search, 
  RefreshCw, 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  Clock,
  Download,
  Filter,
  X,
  Shield,
  TrendingUp,
  Workflow,
  ListChecks
} from 'lucide-react';
import { 
  fetchQuarantineClientes, 
  fetchQuarantineStats,
  fetchQuarantineTrends,
  fetchQuarantineIssueTypes
} from '../../api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import EtlClientesFlow from './EtlClientesFlow';
import EtlServiciosFlow from './EtlServiciosFlow';

const TABS = [
  { key: 'monitor', label: 'Monitor de Calidad', icon: ListChecks },
  { key: 'etl-clientes', label: 'ETL Clientes', icon: Workflow },
  { key: 'etl-servicios', label: 'ETL Servicios', icon: Workflow },
];

export default function DataQualityMonitor() {
  const [activeTab, setActiveTab] = useState('monitor');
  const [quarantineData, setQuarantineData] = useState([]);
  const [stats, setStats] = useState({});
  const [trends, setTrends] = useState([]);
  const [issueTypes, setIssueTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadErrors, setLoadErrors] = useState([]);
  
  // Filtros y búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [issueTypeFilter, setIssueTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);
  
  // Modal de detalles
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Cargar datos iniciales
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadData();
    
    // No auto-refresh según requisitos (se hace manualmente en prod)
  }, []);

  // Recargar cuando cambien filtros o paginación
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (activeTab === 'monitor') {
      loadQuarantineData();
    }
  }, [searchTerm, issueTypeFilter, statusFilter, currentPage, activeTab]);

  const loadData = async () => {
    setLoading(true);
    const errors = [];

    const [statsResult, trendsResult, issueTypesResult] = await Promise.allSettled([
      fetchQuarantineStats(),
      fetchQuarantineTrends(),
      fetchQuarantineIssueTypes()
    ]);

    if (statsResult.status === 'fulfilled') {
      setStats(statsResult.value);
    } else {
      errors.push('No se pudieron cargar los KPIs de calidad (revisa la conexión con el backend).');
      setStats({});
    }

    if (trendsResult.status === 'fulfilled') {
      setTrends(trendsResult.value.trends || []);
    } else {
      errors.push('No se pudo cargar la tendencia de cuarentena.');
      setTrends([]);
    }

    if (issueTypesResult.status === 'fulfilled') {
      setIssueTypes(issueTypesResult.value.issue_types || []);
    } else {
      errors.push('No se pudo cargar el catálogo de tipos de problema.');
      setIssueTypes([]);
    }

    await loadQuarantineData(errors);
    setLoadErrors(errors);
    setLoading(false);
  };

  const loadQuarantineData = async (existingErrors) => {
    const errors = existingErrors ?? [];
    try {
      const offset = (currentPage - 1) * itemsPerPage;
      const filters = {
        issue_type: issueTypeFilter,
        status: statusFilter,
        search: searchTerm,
        limit: itemsPerPage,
        offset: offset
      };

      const data = await fetchQuarantineClientes(filters);
      setQuarantineData(data.records || []);
      setTotalRecords(data.total || 0);
    } catch (error) {
      console.error('❌ Error loading quarantine data:', error);
      errors.push('No se pudo cargar el listado de clientes en cuarentena.');
      setQuarantineData([]);
      setTotalRecords(0);
    }
    if (!existingErrors) {
      setLoadErrors(errors);
    }
  };


  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setIssueTypeFilter('');
    setStatusFilter('');
    setCurrentPage(1);
  };

  const handleExportCSV = () => {
    if (quarantineData.length === 0) return;

    const headers = [
      'ID Cuarentena',
      'Cliente SAP',
      'Razón Social',
      'Tipo de Problema',
      'Descripción',
      'Campos Afectados',
      'Fecha Detección',
      'Estado',
      'Regla de Validación',
      'Batch ID'
    ];

    const csvContent = [
      headers.join(','),
      ...quarantineData.map(record => [
        record.quarantine_id,
        record.cliente_sap,
        `"${record.razon_social}"`,
        record.issue_type,
        `"${record.issue_description}"`,
        record.affected_fields,
        record.detection_timestamp,
        record.quarantine_status,
        record.validation_rule,
        record.batch_id
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    link.href = URL.createObjectURL(blob);
    link.download = `quarantine_${timestamp}.csv`;
    link.click();
  };

  const openDetailModal = (record) => {
    setSelectedRecord(record);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedRecord(null);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending_review: { label: 'Pendiente', color: 'bg-yellow-500', icon: Clock },
      under_correction: { label: 'En Corrección', color: 'bg-blue-500', icon: RefreshCw },
      resolved: { label: 'Resuelto', color: 'bg-green-500', icon: CheckCircle },
      rejected: { label: 'Rechazado', color: 'bg-red-500', icon: XCircle }
    };

    const config = statusConfig[status] || { label: status, color: 'bg-gray-500', icon: AlertTriangle };
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium text-white ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalPages = Math.ceil(totalRecords / itemsPerPage);
  const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endRecord = Math.min(currentPage * itemsPerPage, totalRecords);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#001A12]">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-[#00FF66] animate-spin mx-auto mb-4" />
          <p className="text-[#9fe8b9]">Cargando monitor de calidad...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#001A12] text-white">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#071a0f]/90 backdrop-blur-md border-b border-[#00FF66]/20 shadow-[0_2px_20px_rgba(0,255,102,0.08)]">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-1 h-8 rounded-full bg-gradient-to-b from-[#00FF66] to-[#00ffb3]" />
              <Shield className="w-7 h-7 text-[#00FF66]" />
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-[#00FF66] via-[#00ffb3] to-[#00FF66] bg-clip-text text-transparent">
                  Monitor de Calidad de Datos
                </h1>
                <p className="text-sm text-[#9fe8b9]">Gestión de registros en cuarentena y flujo ETL de clientes</p>
              </div>
            </div>
            {activeTab === 'monitor' && (
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2 bg-[#00FF66]/10 border border-[#00FF66]/30 text-[#00FF66] hover:bg-[#00FF66]/20 hover:border-[#00FF66]/60 rounded-lg [transition:all_.25s_ease]"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                Actualizar
              </button>
            )}
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mt-4">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-t-lg text-sm font-medium border-b-2 [transition:all_.2s_ease] ${
                    isActive
                      ? 'bg-[#00FF66]/10 border-[#00FF66] text-[#00FF66]'
                      : 'border-transparent text-[#9fe8b9] hover:text-[#00FF66] hover:bg-[#00FF66]/5'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {activeTab === 'etl-clientes' ? (
        <EtlClientesFlow />
      ) : activeTab === 'etl-servicios' ? (
        <EtlServiciosFlow />
      ) : (
      <div className="p-6 space-y-6">
        {/* Banner de errores/inconsistencias */}
        {loadErrors.length > 0 && (
          <div className="bg-[#3a1f00] border border-[#ff9b00]/40 rounded-xl p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-[#ff9b00] mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-[#ffd9a0] font-semibold text-sm mb-1">
                Se detectaron problemas al cargar la información. Los KPIs pueden estar incompletos:
              </p>
              <ul className="text-xs text-[#ffd9a0]/80 list-disc list-inside space-y-0.5">
                {loadErrors.map((err, idx) => <li key={idx}>{err}</li>)}
              </ul>
            </div>
          </div>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Total en Cuarentena */}
          <div className="bg-[#00261A] backdrop-blur-md border border-[#ff5c7a]/25 rounded-xl p-4 shadow-lg hover:shadow-[0_0_20px_rgba(255,92,122,0.15)] [transition:all_.2s_ease]">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg border border-[#ff5c7a]/35 bg-[#ff5c7a]/10 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-4 h-4 text-[#ff5c7a]" />
              </div>
              <div className="min-w-0">
                <p className="text-[#ffb3c2] text-xs font-medium truncate">Total en Cuarentena</p>
                <p className="text-xl font-bold text-[#ff5c7a] leading-tight">{stats.total_quarantined ?? 0}</p>
                {typeof stats.total_quarantined_clients === 'number' && (
                  <p className="text-[10px] text-[#ffb3c2]/70">{stats.total_quarantined_clients} clientes únicos</p>
                )}
              </div>
            </div>
          </div>

          {/* Quality Score */}
          <div className="bg-[#00261A] backdrop-blur-md border border-[#00FF66]/25 rounded-xl p-4 shadow-lg hover:shadow-[0_0_20px_rgba(0,255,102,0.15)] hover:border-[#00FF66]/45 [transition:all_.2s_ease]">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg border border-[#00FF66]/35 bg-[#00FF66]/10 flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-4 h-4 text-[#00FF66]" />
              </div>
              <div className="min-w-0">
                <p className="text-[#9fe8b9] text-xs font-medium truncate">Calidad de Datos</p>
                <p className="text-xl font-bold text-[#00FF66] leading-tight">{stats.quality_score?.toFixed(1) ?? 100}%</p>
              </div>
            </div>
          </div>

          {/* Total Clientes */}
          <div className="bg-[#00261A] backdrop-blur-md border border-[#00B8FF]/25 rounded-xl p-4 shadow-lg hover:shadow-[0_0_20px_rgba(0,184,255,0.15)] [transition:all_.2s_ease]">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg border border-[#00B8FF]/35 bg-[#00B8FF]/10 flex items-center justify-center flex-shrink-0">
                <Shield className="w-4 h-4 text-[#00B8FF]" />
              </div>
              <div className="min-w-0">
                <p className="text-[#b3e6ff] text-xs font-medium truncate">Total Clientes</p>
                <p className="text-xl font-bold text-[#00B8FF] leading-tight">{stats.total_clients ?? 0}</p>
              </div>
            </div>
          </div>

          {/* Tipos de Problemas */}
          <div className="bg-[#00261A] backdrop-blur-md border border-[#A56EFF]/25 rounded-xl p-4 shadow-lg hover:shadow-[0_0_20px_rgba(165,110,255,0.15)] [transition:all_.2s_ease]">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg border border-[#A56EFF]/35 bg-[#A56EFF]/10 flex items-center justify-center flex-shrink-0">
                <Filter className="w-4 h-4 text-[#A56EFF]" />
              </div>
              <div className="min-w-0">
                <p className="text-[#d9c9ff] text-xs font-medium truncate">Tipos de Problemas</p>
                <p className="text-xl font-bold text-[#A56EFF] leading-tight">{Object.keys(stats.by_issue_type || {}).length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Trend Chart */}
        {trends.length > 0 && (
          <div className="bg-[#00261A] border border-[#00FF66]/25 rounded-xl p-6 shadow-lg">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-[#00FF66]">
              <TrendingUp className="w-5 h-5" />
              Tendencia de Cuarentena (30 días)
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={trends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#0f3f2b" />
                <XAxis dataKey="date" stroke="#5fbd85" />
                <YAxis stroke="#5fbd85" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#00261A', border: '1px solid rgba(0,255,102,0.3)', borderRadius: '8px' }}
                  labelStyle={{ color: '#e0ffe8' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="count" 
                  stroke="#00FF66" 
                  strokeWidth={2}
                  dot={{ fill: '#10B981', r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Filters and Search */}
        <div className="bg-[#00261A] border border-[#00FF66]/25 rounded-xl p-6 shadow-lg">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#5fbd85]" />
              <input
                type="text"
                placeholder="Buscar por cliente..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 rounded-lg border bg-[#001f16] border-[#00FF66]/25 text-[#e0ffe8] placeholder-[#5fbd85] hover:border-[#00FF66]/55 focus:outline-none focus:border-[#00FF66] focus:shadow-[0_0_8px_rgba(0,255,102,0.2)] [transition:all_.25s_ease]"
              />
            </div>

            {/* Issue Type Filter */}
            <select
              value={issueTypeFilter}
              onChange={(e) => {
                setIssueTypeFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-2 rounded-lg border bg-[#001f16] border-[#00FF66]/25 text-[#e0ffe8] hover:border-[#00FF66]/55 focus:outline-none focus:border-[#00FF66] [transition:all_.25s_ease]"
            >
              <option value="">Todos los tipos</option>
              {issueTypes.map((type) => (
                <option key={type.type} value={type.type}>
                  {type.label} ({type.count})
                </option>
              ))}
            </select>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-2 rounded-lg border bg-[#001f16] border-[#00FF66]/25 text-[#e0ffe8] hover:border-[#00FF66]/55 focus:outline-none focus:border-[#00FF66] [transition:all_.25s_ease]"
            >
              <option value="">Todos los estados</option>
              <option value="pending_review">Pendiente</option>
              <option value="under_correction">En Corrección</option>
              <option value="resolved">Resuelto</option>
              <option value="rejected">Rechazado</option>
            </select>

            {/* Actions */}
            <div className="flex gap-2">
              <button
                onClick={handleClearFilters}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-[#00FF66]/30 text-[#9fe8b9] hover:border-[#00FF66]/55 hover:text-white [transition:all_.2s_ease]"
              >
                <X className="w-4 h-4" />
                Limpiar
              </button>
              <button
                onClick={handleExportCSV}
                disabled={quarantineData.length === 0}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[#00FF66]/15 border border-[#00FF66]/40 text-[#00FF66] hover:bg-[#00FF66]/25 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg [transition:all_.2s_ease]"
              >
                <Download className="w-4 h-4" />
                CSV
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-[#00261A] border border-[#00FF66]/25 rounded-xl shadow-lg overflow-hidden">
          {quarantineData.length === 0 ? (
            <div className="text-center py-16">
              <CheckCircle className="w-16 h-16 text-[#00FF66] mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2 text-white">¡Excelente! No hay registros en cuarentena</h3>
              <p className="text-gray-400">Todos los datos cumplen con las reglas de validación</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#00FF66]/10 border-b border-[#00FF66]/25">
                    <tr>
                      <th className="px-3 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-[#00FF66]">Cliente SAP</th>
                      <th className="px-3 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-[#00FF66]">Razón Social</th>
                      <th className="px-3 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-[#00FF66]">Tipo de Problema</th>
                      <th className="px-3 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-[#00FF66] hidden lg:table-cell">Fecha Detección</th>
                      <th className="px-3 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-[#00FF66]">Estado</th>
                      <th className="px-3 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-[#00FF66] hidden xl:table-cell">Campos Afectados</th>
                      <th className="px-3 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-[#00FF66]">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#00FF66]/10">
                    {quarantineData.map((record, index) => (
                      <tr 
                        key={record.quarantine_id || index}
                        className="hover:bg-[#00FF66]/5 transition-colors"
                      >
                        <td className="px-3 py-2.5 whitespace-nowrap font-medium text-white">{record.cliente_sap}</td>
                        <td className="px-3 py-2.5 max-w-[220px] truncate text-gray-200">{record.razon_social}</td>
                        <td className="px-3 py-2.5">
                          <span className="px-2 py-0.5 bg-[#A56EFF]/20 text-[#d9c9ff] rounded text-xs whitespace-nowrap">
                            {issueTypes.find(t => t.type === record.issue_type)?.label || record.issue_type}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-gray-400 hidden lg:table-cell">
                          {formatDate(record.detection_timestamp)}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          {getStatusBadge(record.quarantine_status)}
                        </td>
                        <td className="px-3 py-2.5 max-w-[200px] truncate text-gray-400 hidden xl:table-cell">{record.affected_fields}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          <button
                            onClick={() => openDetailModal(record)}
                            className="text-[#00FF66] hover:text-[#6dffb0] font-medium"
                          >
                            Ver
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="bg-[#001f16] px-4 py-3 flex flex-wrap items-center justify-between gap-2 border-t border-[#00FF66]/15">
                <div className="text-xs text-gray-400">
                  Mostrando {startRecord} - {endRecord} de {totalRecords} registros
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 text-xs border border-[#00FF66]/25 text-[#9fe8b9] hover:border-[#00FF66]/55 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed rounded-lg [transition:all_.2s_ease]"
                  >
                    Anterior
                  </button>
                  <span className="px-3 py-1.5 text-xs bg-[#00FF66]/10 border border-[#00FF66]/25 text-[#00FF66] rounded-lg">
                    Página {currentPage} de {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 text-xs border border-[#00FF66]/25 text-[#9fe8b9] hover:border-[#00FF66]/55 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed rounded-lg [transition:all_.2s_ease]"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedRecord && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#00261A] border border-[#00FF66]/25 rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-[#071a0f] border-b border-[#00FF66]/25 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-[#00FF66]">Detalles del Registro en Cuarentena</h3>
              <button
                onClick={closeDetailModal}
                className="p-2 hover:bg-[#00FF66]/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Información Principal */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400">ID Cuarentena</label>
                  <p className="text-lg font-medium">{selectedRecord.quarantine_id}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-400">Cliente SAP</label>
                  <p className="text-lg font-medium">{selectedRecord.cliente_sap}</p>
                </div>
                <div className="col-span-2">
                  <label className="text-sm text-gray-400">Razón Social</label>
                  <p className="text-lg font-medium">{selectedRecord.razon_social}</p>
                </div>
              </div>

              {/* Problema */}
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <h4 className="font-semibold text-red-400 mb-2">Problema Detectado</h4>
                <p className="text-sm mb-2">{selectedRecord.issue_description}</p>
                <div className="flex gap-4 text-sm">
                  <div>
                    <span className="text-gray-400">Tipo:</span>{' '}
                    <span className="text-orange-300">
                      {issueTypes.find(t => t.type === selectedRecord.issue_type)?.label || selectedRecord.issue_type}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400">Regla:</span>{' '}
                    <span className="text-orange-300">{selectedRecord.validation_rule}</span>
                  </div>
                </div>
              </div>

              {/* Campos Afectados */}
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Campos Afectados</label>
                <div className="flex flex-wrap gap-2">
                  {selectedRecord.affected_fields?.split(',').map((field, idx) => (
                    <span key={idx} className="px-3 py-1 bg-yellow-500/20 text-yellow-300 rounded-full text-sm">
                      {field.trim()}
                    </span>
                  ))}
                </div>
              </div>

              {/* Metadata */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <label className="text-gray-400">Fecha Detección</label>
                  <p>{formatDate(selectedRecord.detection_timestamp)}</p>
                </div>
                <div>
                  <label className="text-gray-400">Estado</label>
                  <div className="mt-1">{getStatusBadge(selectedRecord.quarantine_status)}</div>
                </div>
                <div>
                  <label className="text-gray-400">Batch ID</label>
                  <p className="font-mono text-xs">{selectedRecord.batch_id}</p>
                </div>
              </div>

              {/* Datos Originales */}
              {selectedRecord.original_data && (
                <div>
                  <label className="text-sm text-gray-400 mb-2 block">Datos Originales (JSON)</label>
                  <pre className="bg-gray-900 p-4 rounded-lg text-xs overflow-x-auto">
                    {JSON.stringify(selectedRecord.original_data, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
